import { useEffect, useRef, useState, type RefObject } from "react";
import { useEditor, type JSONContent } from "@tiptap/react";
import type { Editor } from "./canvas";
import type { CanvasEditor } from "./canvas/engine/editor";
import type { TLRichText, TLShapePartial } from "@tldraw/tlschema";
import type { BoardDocument } from "../shared/schema";
import { contentBytes, MAX_CONTENT_BYTES } from "../shared/file-content";
import { DOCUMENT_LEASE_MS, documentWriter } from "../shared/document-session";
import { documentTextExtensions } from "./document-richtext";

type Draft = {
  name: string;
  richText: TLRichText;
  capturedAt: number;
};
const localKey = (room: string, user: string, id: string) =>
  `calque:document-draft:${room}:${user}:${id}`;
function readDraft(key: string): Draft | null {
  try {
    const value = window.localStorage.getItem(key);
    const parsed = value ? (JSON.parse(value) as Draft) : null;
    return parsed &&
      typeof parsed.name === "string" &&
      parsed.richText?.type === "doc"
      ? parsed
      : null;
  } catch {
    return null;
  }
}
function writeDraft(key: string, draft: Draft) {
  try {
    window.localStorage.setItem(key, JSON.stringify(draft));
    return true;
  } catch {
    return false;
  }
}

export function useDocumentWriting({
  editor,
  roomId,
  userId,
  shape,
  initial,
  sessionId,
  connected,
  canEdit,
  initiallyEdit,
  initialFocusTitle = initiallyEdit,
  titleInput,
  focusPoint,
}: {
  editor: Editor;
  roomId: string;
  userId: string;
  shape: BoardDocument | null;
  initial: BoardDocument;
  sessionId: string | null;
  connected: boolean;
  canEdit: boolean;
  initiallyEdit: boolean;
  initialFocusTitle?: boolean;
  titleInput: RefObject<HTMLTextAreaElement | null>;
  focusPoint?: { x: number; y: number; bodyTop?: number };
}) {
  const key = localKey(roomId, userId, initial.id);
  const [title, setTitle] = useState(initial.props.name);
  const [editing, setEditing] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [issue, setIssue] = useState("");
  const [draft, setDraft] = useState(() => readDraft(key));
  const [now, setNow] = useState(Date.now());
  const connectedRef = useRef(connected),
    sessionRef = useRef(sessionId);
  const applyingRemote = useRef(false);
  const focusTitleOnFirstWrite = useRef(initialFocusTitle);
  connectedRef.current = connected;
  sessionRef.current = sessionId;
  const owner = documentWriter(shape, now);
  const canWrite =
    !!shape &&
    connected &&
    editing &&
    owner === sessionId &&
    !!sessionId &&
    canEdit &&
    !shape.isLocked;
  function updateDocument(update: TLShapePartial<BoardDocument>) {
    editor.run(() => editor.updateShape<BoardDocument>(update), {
      history: "ignore",
    });
  }

  const textEditor = useEditor(
    {
      extensions: documentTextExtensions,
      content: (shape?.props.richText ??
        initial.props.richText) as unknown as JSONContent,
      editable: false,
      editorProps: { attributes: { "aria-label": "Document body" } },
      onUpdate: ({ editor: tiptap }) => {
        if (applyingRemote.current) return;
        const richText = tiptap.getJSON() as TLRichText;
        const current = editor.getShape<BoardDocument>(initial.id);
        if (
          current &&
          JSON.stringify(richText) === JSON.stringify(current.props.richText)
        )
          return;
        const saved = writeDraft(key, {
          name: current?.props.name || initial.props.name,
          richText,
          capturedAt: Date.now(),
        });
        if (!saved)
          setIssue("Browser storage is full. Copy your text before leaving.");
        setDraft(readDraft(key));
        if (contentBytes(richText) > MAX_CONTENT_BYTES) {
          setIssue(
            "Document limit reached. Your text is kept on this device; split it into another document.",
          );
          return;
        }
        if (
          !current ||
          !connectedRef.current ||
          documentWriter(current) !== sessionRef.current
        ) {
          setIssue(
            "Changes are on this device. Reconnect or open a recovered copy.",
          );
          return;
        }
        updateDocument({
          id: current.id,
          type: "document",
          props: { richText },
          meta: {
            ...current.meta,
            documentVersion: Number(current.meta.documentVersion ?? 0) + 1,
          },
        });
        setIssue("");
      },
      // Each writing session gets fresh text history so undo cannot replay a
      // previous writer's changes after the document has been read or refreshed.
    },
    [initial.id, editing],
  );
  const currentTextEditor = useRef(textEditor);
  currentTextEditor.current = textEditor;

  useEffect(() => {
    if (!canWrite || !textEditor) return;
    const canvas = editor as unknown as CanvasEditor;
    canvas.setRichTextEditor(textEditor);
    const changed = () => canvas.history.touch();
    textEditor.on("transaction", changed);
    return () => {
      textEditor.off("transaction", changed);
      if (canvas.getRichTextEditor() === textEditor) canvas.setRichTextEditor(null);
    };
  }, [canWrite, textEditor, editor]);

  useEffect(() => {
    if (textEditor && !textEditor.isDestroyed) textEditor.setEditable(canWrite);
  }, [textEditor, canWrite]);
  useEffect(() => {
    if (!textEditor || textEditor.isDestroyed || editing || !shape) return;
    if (
      JSON.stringify(textEditor.getJSON()) !==
      JSON.stringify(shape.props.richText)
    ) {
      applyingRemote.current = true;
      try {
        textEditor.commands.setContent(
          shape.props.richText as unknown as JSONContent,
          { emitUpdate: false },
        );
      } finally {
        applyingRemote.current = false;
      }
    }
    setTitle(shape.props.name);
  }, [shape?.props.richText, shape?.props.name, textEditor, editing]);
  useEffect(() => {
    if (!requesting || !sessionId || !connected || owner !== sessionId) return;
    setRequesting(false);
    setEditing(true);
  }, [requesting, sessionId, connected, owner]);
  useEffect(() => {
    if (!canWrite || !textEditor || textEditor.isDestroyed) return;
    const focusTitle = focusTitleOnFirstWrite.current;
    const timer = window.setTimeout(() => {
      if (textEditor.isDestroyed) return;
      focusTitleOnFirstWrite.current = false;
      if (focusTitle) {
        titleInput.current?.focus();
        titleInput.current?.select();
      } else {
        textEditor.commands.focus("end");
        if (focusPoint) {
          const shift =
            focusPoint.bodyTop === undefined
              ? 0
              : textEditor.view.dom.getBoundingClientRect().top -
                focusPoint.bodyTop;
          const hit = textEditor.view.posAtCoords({
            left: focusPoint.x,
            top: focusPoint.y + shift,
          });
          if (hit) textEditor.commands.setTextSelection(hit.pos);
        }
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [canWrite, textEditor]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
      const live = editor.getShape<BoardDocument>(initial.id);
      if (
        !editing ||
        !connectedRef.current ||
        !sessionRef.current ||
        documentWriter(live) !== sessionRef.current
      )
        return;
      updateDocument({
        id: live!.id,
        type: "document",
        meta: {
          ...live!.meta,
          documentWriterUntil: Date.now() + DOCUMENT_LEASE_MS,
        },
      });
    }, 20_000);
    return () => window.clearInterval(timer);
  }, [editing, editor, initial.id]);
  useEffect(() => {
    if (
      !initiallyEdit ||
      !shape ||
      !sessionId ||
      !connected ||
      requesting ||
      editing
    )
      return;
    requestEdit();
    // Initial creation requests the writer once; later state changes use Edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiallyEdit, !!shape, sessionId, connected]);
  useEffect(() => () => releaseWriter(), [editor, initial.id]);

  function requestEdit() {
    const live = editor.getShape<BoardDocument>(initial.id);
    if (!live || !canEdit || live.isLocked) return;
    if (!connected || !sessionId) {
      setIssue(
        "Connect to start editing. Your existing draft is available below.",
      );
      return;
    }
    const currentOwner = documentWriter(live);
    if (currentOwner && currentOwner !== sessionId) {
      setIssue("Another editor is writing this document. You can read it now.");
      return;
    }
    setRequesting(true);
    updateDocument({
      id: live.id,
      type: "document",
      meta: {
        ...live.meta,
        documentWriter: sessionId,
        documentWriterBaseVersion: live.meta.documentVersion,
        documentWriterUntil: Date.now() + DOCUMENT_LEASE_MS,
      },
    });
  }
  function releaseWriter() {
    const live = editor.getShape<BoardDocument>(initial.id);
    if (
      live &&
      connectedRef.current &&
      sessionRef.current &&
      documentWriter(live) === sessionRef.current
    ) {
      const activeEditor = currentTextEditor.current;
      const richText =
        activeEditor && !activeEditor.isDestroyed
          ? (activeEditor.getJSON() as TLRichText)
          : live.props.richText;
      const changed =
        JSON.stringify(richText) !== JSON.stringify(live.props.richText);
      const withinLimit = contentBytes(richText) <= MAX_CONTENT_BYTES;
      if (changed) {
        writeDraft(key, {
          name: live.props.name,
          richText,
          capturedAt: Date.now(),
        });
      }
      updateDocument({
        id: live.id,
        type: "document",
        ...(changed && withinLimit ? { props: { richText } } : {}),
        meta: {
          ...live.meta,
          documentWriter: null,
          documentWriterUntil: 0,
          documentVersion:
            Number(live.meta.documentVersion) +
            (changed && withinLimit ? 1 : 0),
        },
      });
    }
  }
  function finishEdit() {
    releaseWriter();
    setDraft(readDraft(key));
    setEditing(false);
  }
  const unresolved =
    !!draft &&
    (!shape ||
      JSON.stringify(draft.richText) !== JSON.stringify(shape.props.richText));
  return {
    textEditor,
    title,
    setTitle,
    editing,
    requesting,
    canWrite,
    owner,
    issue,
    draft,
    unresolved,
    requestEdit,
    finishEdit,
  };
}
